import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Ruler, TrendingUp, Heart, Trash2 } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { useFavourites } from "../lib/favourites";

const CURR_LABEL = { INR: "₹", USD: "$", AED: "AED " };
function fmtPrice(inr, curr) {
  const fx = curr === "USD" ? 1 / 84 : curr === "AED" ? 1 / 22.9 : 1;
  const v = inr * fx;
  if (v >= 10000000 && curr === "INR") return `${CURR_LABEL[curr]}${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 1000000) return `${CURR_LABEL[curr]}${(v / 1000000).toFixed(2)}M`;
  return `${CURR_LABEL[curr]}${v.toFixed(0)}`;
}

export default function Favourites({ currency }) {
  const { favIds, toggleFav } = useFavourites();
  const [all, setAll] = useState([]);

  useEffect(() => {
    api.get("/properties").then((r) => setAll(r.data)).catch(() => {});
  }, []);

  const items = all.filter((p) => favIds.includes(p.id));

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3">
          <span className="accent-dot" /> Saved Properties
        </div>
        <h1 className="display-1 max-w-6xl" data-testid="favourites-title">
          <MaskedLines lines={["Your shortlist."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Properties you've bookmarked are saved locally on this device. No login required — your shortlist stays with you.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-20 pb-24" data-testid="favourites-list">
        {items.length === 0 ? (
          <Reveal>
            <div className="border border-[#EDE6D3]/15 p-12 md:p-20 text-center">
              <Heart size={40} className="mx-auto text-[#EDE6D3]/20 mb-6" />
              <h3 className="font-serif text-[28px] leading-tight">No saved properties yet</h3>
              <p className="mt-4 text-[14px] text-[#EDE6D3]/60 max-w-md mx-auto">
                Browse the portfolio and tap the heart icon on any property to save it here.
              </p>
              <Link to="/properties" className="btn-primary mt-8 inline-flex" data-testid="browse-properties-btn">
                Browse Properties <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50 mb-8">{items.length} saved {items.length === 1 ? "property" : "properties"}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {items.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <div className="card-sand p-4 group relative" data-testid={`fav-card-${p.id}`}>
                    <button
                      onClick={() => toggleFav(p.id)}
                      className="absolute top-6 right-6 z-10 w-8 h-8 flex items-center justify-center bg-[#0A2342]/80 border border-[#EDE6D3]/30 text-[#EDE6D3]/70 hover:bg-red-900/60 hover:border-red-400 transition-all"
                      data-testid={`fav-remove-${p.id}`}
                      title="Remove from shortlist"
                    >
                      <Trash2 size={14} />
                    </button>
                    <Link to={`/properties/${p.id}`} className="block">
                      <div className="relative a-4-5 overflow-hidden">
                        <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                      </div>
                      <div className="p-4 pt-6">
                        <h3 className="font-serif text-[24px] leading-tight">{p.title}</h3>
                        <div className="mt-2 flex items-center gap-2 text-[13px] text-[#EDE6D3]/70"><MapPin size={13} /> {p.location}</div>
                        <div className="mt-5 grid grid-cols-3 gap-2 text-[11px] text-[#EDE6D3]/75">
                          <div><div className="eyebrow text-[#EDE6D3]/40">Price</div><div className="font-serif text-[17px] mt-1">{fmtPrice(p.price_inr, currency)}</div></div>
                          <div><div className="eyebrow text-[#EDE6D3]/40 flex items-center gap-1"><Ruler size={10} /> Area</div><div className="font-serif text-[17px] mt-1">{p.area_sqft.toLocaleString()}<span className="text-[10px] ml-1">sqft</span></div></div>
                          <div><div className="eyebrow text-[#EDE6D3]/40 flex items-center gap-1"><TrendingUp size={10} /> Yield</div><div className="font-serif text-[17px] mt-1">{p.yield_pct ? `${p.yield_pct}%` : "—"}</div></div>
                        </div>
                        <div className="mt-6 flex items-center justify-end border-t border-[#EDE6D3]/15 pt-4">
                          <div className="border border-[#EDE6D3]/30 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] transition-colors p-2"><ArrowUpRight size={16} /></div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
